import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Trash2, Edit2, X, Users, Dumbbell, Salad, CreditCard, TrendingUp,
  ChevronRight, ChevronDown, Save, AlertCircle, CheckCircle, KeyRound, Copy, Search, Filter, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import WorkoutLogTable from "./WorkoutLogTable";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function api(token) {
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
}

function ProfileImageUpload({ token, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api(token).post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };
  return (
    <label className="mt-1 block text-center text-[9px] text-zinc-500 hover:text-gg-red cursor-pointer transition-colors">
      {uploading ? "..." : "Change photo"}
      <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
    </label>
  );
}

function calcAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age > 0 ? age : null;
}

export default function AdminClientsTab({ token }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterAge, setFilterAge] = useState("all");

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await api(token).get("/admin/clients");
      setClients(data);
    } catch { toast.error("Failed to load members"); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const selectClient = async (cid) => {
    if (selected === cid) { setSelected(null); setDetail(null); return; }
    setSelected(cid);
    try {
      const { data } = await api(token).get(`/admin/clients/${cid}`);
      setDetail(data);
    } catch { toast.error("Failed to load member"); }
  };

  const deleteClient = async (cid) => {
    if (!window.confirm("Delete this member and all their data?")) return;
    try {
      await api(token).delete(`/admin/clients/${cid}`);
      toast.success("Member deleted");
      setSelected(null);
      setDetail(null);
      fetchClients();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-gg-red border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-clients-section">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold uppercase tracking-wide flex items-center gap-2"><Users size={18} className="text-gg-red" /> Members ({clients.length})</h2>
        <Button onClick={() => setShowAdd(!showAdd)} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="add-client-btn">
          <Plus size={14} className="mr-1" /> Add Member
        </Button>
      </div>

      {clients.length > 0 && (
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or phone..."
                className="bg-gg-surface border-white/10 text-white rounded-lg h-9 pl-9 text-xs" data-testid="client-search-input" />
              {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"><X size={12} /></button>}
            </div>
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 text-[10px] font-heading uppercase tracking-wider px-3 py-2 rounded-lg transition-all shrink-0 ${showFilters || filterStatus !== "all" || filterType !== "all" || filterAge !== "all" ? "bg-gg-red text-white" : "bg-gg-surface text-zinc-500 hover:text-white border border-white/10"}`}
              data-testid="filter-toggle">
              <Filter size={12} /> Filters
              {(filterStatus !== "all" || filterType !== "all" || filterAge !== "all") && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
            </button>
          </div>
          {showFilters && (
            <div className="flex flex-wrap gap-2 items-center bg-gg-surface border border-white/5 rounded-lg p-3">
              <div>
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Status</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-gg-black border border-white/10 text-white rounded-lg h-8 px-2 text-[11px]" data-testid="filter-status">
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Training</label>
                <select value={filterType} onChange={e => setFilterType(e.target.value)} className="bg-gg-black border border-white/10 text-white rounded-lg h-8 px-2 text-[11px]" data-testid="filter-type">
                  <option value="all">All</option>
                  <option value="personal">Personal</option>
                  <option value="online">Online</option>
                  <option value="nutrition">Nutrition</option>
                  <option value="group">Group</option>
                </select>
              </div>
              <div>
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Age</label>
                <select value={filterAge} onChange={e => setFilterAge(e.target.value)} className="bg-gg-black border border-white/10 text-white rounded-lg h-8 px-2 text-[11px]" data-testid="filter-age">
                  <option value="all">All</option>
                  <option value="under18">Under 18</option>
                  <option value="18-25">18–25</option>
                  <option value="26-35">26–35</option>
                  <option value="36-45">36–45</option>
                  <option value="46+">46+</option>
                </select>
              </div>
              {(filterStatus !== "all" || filterType !== "all" || filterAge !== "all") && (
                <button onClick={() => { setFilterStatus("all"); setFilterType("all"); setFilterAge("all"); }}
                  className="text-[10px] text-gg-red hover:underline ml-auto">Clear filters</button>
              )}
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {showAdd && <AddClientForm token={token} onDone={() => { setShowAdd(false); fetchClients(); }} onCancel={() => setShowAdd(false)} />}
      </AnimatePresence>

      {clients.length === 0 ? (
        <div className="text-center py-12">
          <Users size={40} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No members yet. Add your first member above.</p>
        </div>
      ) : (() => {
        const q = search.toLowerCase().trim();
        let filtered = clients;
        if (q) filtered = filtered.filter(c =>
          c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q)
        );
        if (filterStatus !== "all") filtered = filtered.filter(c => (c.payment_status || "inactive") === filterStatus);
        if (filterType !== "all") filtered = filtered.filter(c => c.training_type === filterType);
        if (filterAge !== "all") {
          filtered = filtered.filter(c => {
            const age = calcAge(c.date_of_birth) || c.age;
            if (!age) return false;
            if (filterAge === "under18") return age < 18;
            if (filterAge === "18-25") return age >= 18 && age <= 25;
            if (filterAge === "26-35") return age >= 26 && age <= 35;
            if (filterAge === "36-45") return age >= 36 && age <= 45;
            if (filterAge === "46+") return age >= 46;
            return true;
          });
        }
        const activeFilters = [filterStatus !== "all" && filterStatus, filterType !== "all" && filterType, filterAge !== "all" && filterAge].filter(Boolean);
        return (
          <div className="space-y-2">
            {filtered.length === 0 && <p className="text-zinc-500 text-xs text-center py-6">{q || activeFilters.length ? `No members matching filters` : `No members`}</p>}
            {filtered.length > 0 && filtered.length !== clients.length && <p className="text-zinc-600 text-[10px]">Showing {filtered.length} of {clients.length} members</p>}
            {filtered.map((c) => (
            <div key={c.id}>
              <button onClick={() => selectClient(c.id)}
                data-testid={`client-row-${c.id}`}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  selected === c.id ? "bg-gg-surface border-gg-red/30" : "bg-gg-surface border-white/5 hover:border-white/10"
                }`}>
                <div className="w-9 h-9 rounded-full bg-gg-red/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {c.profile_image ? (
                    <img src={c.profile_image} alt={c.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-gg-red text-sm font-bold">{c.name?.[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{c.name}{(() => { const a = calcAge(c.date_of_birth) || c.age; return a ? <span className="text-zinc-500 text-xs font-normal ml-1">({a}y)</span> : null; })()}</p>
                  <p className="text-xs text-zinc-500 truncate">{c.email} | {c.phone}</p>
                </div>
                {c.training_type && (
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-heading uppercase ${
                    c.training_type === "online" ? "bg-blue-500/10 text-blue-400" :
                    c.training_type === "nutrition" ? "bg-amber-500/10 text-amber-400" :
                    c.training_type === "group" ? "bg-teal-500/10 text-teal-400" :
                    "bg-purple-500/10 text-purple-400"
                  }`}>{c.training_type}</span>
                )}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-heading uppercase ${
                  c.payment_status === "active" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
                }`}>{c.payment_status || "inactive"}</span>
                {selected === c.id ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronRight size={14} className="text-zinc-500" />}
              </button>

              <AnimatePresence>
                {selected === c.id && detail && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                    <ClientDetail token={token} detail={detail} clientId={c.id} onRefresh={() => { selectClient(c.id); fetchClients(); }} onDelete={() => deleteClient(c.id)} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          </div>
        );
      })()}
    </div>
  );
}

// ==================== ADD CLIENT FORM ====================
function AddClientForm({ token, onDone, onCancel }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [trainingType, setTrainingType] = useState("personal");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api(token).post("/admin/clients", { name, email, phone, password, date_of_birth: dob || null, training_type: trainingType });
      toast.success("Member created!");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  return (
    <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      onSubmit={handleSubmit} className="bg-gg-surface rounded-xl p-4 border border-white/5 space-y-3" data-testid="add-client-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} required className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="add-client-name" />
        <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="add-client-email" />
        <Input placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)} required className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="add-client-phone" />
        <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="add-client-password" />
        <Input type="date" value={dob} onChange={e => setDob(e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-10" data-testid="add-client-dob" />
        <select value={trainingType} onChange={e => setTrainingType(e.target.value)} className="bg-gg-black border border-white/10 text-white rounded-lg h-10 px-3 text-sm" data-testid="add-client-training-type">
          <option value="personal">Personal Training</option>
          <option value="online">Online Training</option>
          <option value="nutrition">Nutrition</option>
          <option value="group">Group Training</option>
        </select>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="add-client-submit">{saving ? "..." : "Create"}</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="text-zinc-400">Cancel</Button>
      </div>
    </motion.form>
  );
}

// ==================== CLIENT DETAIL PANEL ====================
function ClientDetail({ token, detail, clientId, onRefresh, onDelete }) {
  const [subtab, setSubtab] = useState("workout");

  return (
    <div className="ml-0 sm:ml-4 mt-2 bg-gg-black rounded-xl border border-white/5 p-3 sm:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
          {["workout", "log", "diet", "payment", "progress", "password", "profile"].map(t => (
            <button key={t} onClick={() => setSubtab(t)}
              className={`text-[9px] font-heading uppercase tracking-wider px-2.5 py-1.5 rounded-lg transition-all whitespace-nowrap shrink-0 ${
                subtab === t ? "bg-gg-red text-white" : "bg-gg-surface text-zinc-500 hover:text-white"
              }`}>{t === "password" ? "Account" : t === "log" ? "Log" : t}</button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500 hover:text-red-400" data-testid="delete-client-btn">
          <Trash2 size={14} />
        </Button>
      </div>

      {subtab === "profile" && <ProfileEditor token={token} clientId={clientId} client={detail.client} onSave={onRefresh} />}
      {subtab === "workout" && <WorkoutEditor token={token} clientId={clientId} existing={detail.workout_plan} onSave={onRefresh} />}
      {subtab === "log" && <WorkoutLogTable token={token} clientId={clientId} isTrainer={true} />}
      {subtab === "diet" && <DietEditor token={token} clientId={clientId} existing={detail.diet_plan} onSave={onRefresh} />}
      {subtab === "payment" && <PaymentEditor token={token} clientId={clientId} client={detail.client} payments={detail.payments} onSave={onRefresh} />}
      {subtab === "progress" && <ProgressViewer token={token} clientId={clientId} entries={detail.progress} onSave={onRefresh} />}
      {subtab === "password" && <ResetPasswordForm token={token} clientId={clientId} clientName={detail.client?.name} clientEmail={detail.client?.email} onEmailChange={onRefresh} />}
    </div>
  );
}

// ==================== PROFILE EDITOR ====================
function ProfileEditor({ token, clientId, client, onSave }) {
  const [name, setName] = useState(client?.name || "");
  const [dob, setDob] = useState(client?.date_of_birth || "");
  const [phone, setPhone] = useState(client?.phone || "");
  const [trainingType, setTrainingType] = useState(client?.training_type || "personal");
  const [profileImage, setProfileImage] = useState(client?.profile_image || "");
  const [saving, setSaving] = useState(false);

  const age = calcAge(dob);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api(token).put(`/admin/clients/${clientId}/profile`, {
        name: name.trim() || undefined,
        date_of_birth: dob || undefined,
        phone: phone.trim() || undefined,
        training_type: trainingType,
        profile_image: profileImage || undefined
      });
      toast.success("Profile updated!");
      onSave();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="profile-editor">
      {/* Profile Photo */}
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          <div className="w-16 h-16 rounded-full bg-gg-surface border border-white/10 overflow-hidden flex items-center justify-center">
            {profileImage ? (
              <img src={profileImage} alt={name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-gg-red text-xl font-bold">{name?.[0]?.toUpperCase() || "?"}</span>
            )}
          </div>
          <ProfileImageUpload token={token} onUploaded={(url) => setProfileImage(url)} />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">{name || "Member"}</p>
          {age && <p className="text-zinc-500 text-xs">{age} years old</p>}
        </div>
      </div>
      {/* Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Name</label>
          <Input value={name} onChange={e => setName(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Date of Birth{age ? ` (${age}y)` : ""}</label>
          <Input type="date" value={dob} onChange={e => setDob(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-sm" data-testid="profile-dob" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Phone</label>
          <Input value={phone} onChange={e => setPhone(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Training Type</label>
          <select value={trainingType} onChange={e => setTrainingType(e.target.value)} className="w-full bg-gg-surface border border-white/10 text-white rounded-lg h-9 px-3 text-sm" data-testid="profile-training-type">
            <option value="personal">Personal Training</option>
            <option value="online">Online Training</option>
            <option value="nutrition">Nutrition</option>
            <option value="group">Group Training</option>
          </select>
        </div>
      </div>
      <Button onClick={handleSave} disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="save-profile-btn">
        <Save size={14} className="mr-1" /> {saving ? "..." : "Save Profile"}
      </Button>
    </div>
  );
}

// ==================== COPY FROM CLIENT ====================
function CopyFromClient({ token, type, currentClientId, onCopy }) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const fetchPlans = async () => {
    if (plans.length > 0) { setOpen(!open); return; }
    setLoading(true);
    try {
      const { data } = await api(token).get("/admin/clients/plans-summary");
      setPlans(data.filter(p => p.id !== currentClientId && (type === "workout" ? p.has_workout : p.has_diet)));
    } catch { toast.error("Failed to load plans"); }
    setLoading(false);
    setOpen(true);
  };

  const handleCopy = (plan) => {
    onCopy(type === "workout" ? plan.workout_plan : plan.diet_plan);
    setOpen(false);
    setQ("");
    toast.success(`Copied ${type} plan from ${plan.name}`);
  };

  const filtered = q ? plans.filter(p => p.name.toLowerCase().includes(q.toLowerCase())) : plans;

  return (
    <div className="relative">
      <Button type="button" variant="outline" size="sm" onClick={fetchPlans}
        className="border-white/10 text-zinc-400 hover:text-white rounded-lg text-[10px] h-8"
        data-testid={`copy-${type}-btn`}>
        <Copy size={12} className="mr-1" /> {loading ? "..." : `Copy from member`}
      </Button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
            className="absolute top-10 right-0 z-50 bg-gg-surface border border-white/10 rounded-xl shadow-2xl shadow-black/50 w-72">
            {plans.length > 2 && (
              <div className="p-2 border-b border-white/5">
                <div className="relative">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search member..."
                    className="w-full bg-gg-black border border-white/10 text-white rounded-lg h-7 pl-7 pr-2 text-[10px] outline-none focus:border-gg-red/50"
                    data-testid={`copy-${type}-search`} autoFocus />
                </div>
              </div>
            )}
            <div className="max-h-44 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-zinc-500 text-xs p-3 text-center">{q ? "No match" : `No other members have ${type} plans`}</p>
              ) : (
                filtered.map((p) => (
                  <button key={p.id} onClick={() => handleCopy(p)}
                    className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                    data-testid={`copy-from-${p.id}`}>
                    <p className="text-white text-xs font-semibold">{p.name}</p>
                    <p className="text-zinc-500 text-[10px]">{type === "workout" ? p.workout_name : p.diet_name}</p>
                  </button>
                ))
              )}
            </div>
            <button onClick={() => { setOpen(false); setQ(""); }} className="w-full text-center text-zinc-600 text-[10px] py-1.5 hover:text-white border-t border-white/5">Close</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Helper: ensure exercise has a set_groups array (back-compat for legacy {sets, reps})
const ensureSetGroups = (ex) => {
  if (Array.isArray(ex.set_groups) && ex.set_groups.length > 0) return ex.set_groups;
  if (ex.sets || ex.reps) return [{ sets: ex.sets || "", reps: ex.reps || "" }];
  return [{ sets: "", reps: "" }];
};

// ==================== WORKOUT EDITOR ====================
function WorkoutEditor({ token, clientId, existing, onSave }) {
  const [planName, setPlanName] = useState(existing?.plan_name || "");
  const normalizeDays = (rawDays) => (rawDays || []).map((d) => ({
    ...d,
    exercises: (d.exercises || []).map((ex) => ({ name: ex.name || "", set_groups: ensureSetGroups(ex) })),
  }));
  const defaultDays = normalizeDays(existing?.weeks?.[0]?.days || existing?.days) || [{ name: "Push Day", focus: "Chest, Shoulders & Triceps", exercises: [{ name: "", set_groups: [{ sets: "", reps: "" }] }] }];
  const [days, setDays] = useState(defaultDays.length ? defaultDays : [{ name: "Push Day", focus: "", exercises: [{ name: "", set_groups: [{ sets: "", reps: "" }] }] }]);
  const [saving, setSaving] = useState(false);

  const loadFromPlan = (plan) => {
    if (!plan) return;
    setPlanName(plan.plan_name || "");
    const norm = normalizeDays(plan.days || plan.weeks?.[0]?.days);
    setDays(norm.length ? norm : [{ name: "Push Day", focus: "", exercises: [{ name: "", set_groups: [{ sets: "", reps: "" }] }] }]);
  };

  const updateDay = (di, field, value) => {
    const d = [...days]; d[di][field] = value; setDays(d);
  };

  const updateExerciseName = (di, ei, value) => {
    const d = [...days]; d[di].exercises[ei].name = value; setDays(d);
  };

  const updateSetGroup = (di, ei, gi, field, value) => {
    const d = [...days]; d[di].exercises[ei].set_groups[gi][field] = value; setDays(d);
  };

  const addSetGroup = (di, ei) => {
    const d = [...days];
    const groups = d[di].exercises[ei].set_groups || [{ sets: "", reps: "" }];
    d[di].exercises[ei].set_groups = [...groups, { sets: "", reps: "" }];
    setDays(d);
  };

  const removeSetGroup = (di, ei, gi) => {
    const d = [...days];
    const groups = d[di].exercises[ei].set_groups || [];
    if (groups.length > 1) {
      d[di].exercises[ei].set_groups = groups.filter((_, i) => i !== gi);
      setDays(d);
    }
  };

  const addExercise = (di) => {
    const d = [...days]; d[di].exercises.push({ name: "", set_groups: [{ sets: "", reps: "" }] }); setDays(d);
  };

  const removeExercise = (di, ei) => {
    const d = [...days]; d[di].exercises.splice(ei, 1); setDays(d);
  };

  const addDay = () => {
    setDays([...days, { name: "", focus: "", exercises: [{ name: "", set_groups: [{ sets: "", reps: "" }] }] }]);
  };

  const removeDay = (di) => {
    setDays(days.filter((_, i) => i !== di));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Persist set_groups; also keep legacy sets/reps populated from the first group for back-compat.
      const cleanedDays = days.map((d) => ({
        ...d,
        exercises: (d.exercises || []).map((ex) => {
          const groups = (ex.set_groups || []).filter((g) => g.sets || g.reps);
          const finalGroups = groups.length ? groups : [{ sets: "", reps: "" }];
          return {
            name: ex.name || "",
            set_groups: finalGroups,
            sets: finalGroups[0]?.sets || "",
            reps: finalGroups[0]?.reps || "",
          };
        }),
      }));
      await api(token).put(`/admin/clients/${clientId}/workout-plan`, { plan_name: planName, weeks: [{ days: cleanedDays }] });
      toast.success("Workout plan saved!");
      onSave();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-3" data-testid="workout-editor">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Input placeholder="Plan Name (e.g., PPL Split, Full Body)" value={planName} onChange={e => setPlanName(e.target.value)}
          className="bg-gg-surface border-white/10 text-white rounded-lg h-10 flex-1" data-testid="workout-plan-name" />
        <CopyFromClient token={token} type="workout" currentClientId={clientId} onCopy={loadFromPlan} />
      </div>
      {days.map((day, di) => (
        <div key={di} className="bg-gg-surface rounded-lg p-3 border border-white/5 space-y-2">
          <div className="flex gap-2 items-center">
            <Input placeholder="Training name (e.g., Push Day, Leg Day, Cardio)" value={day.name} onChange={e => updateDay(di, "name", e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-8 text-xs flex-1" />
            <Input placeholder="Focus (e.g., Chest & Triceps)" value={day.focus} onChange={e => updateDay(di, "focus", e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-8 text-xs flex-1" />
            {days.length > 1 && <button onClick={() => removeDay(di)} className="text-zinc-600 hover:text-red-400 shrink-0"><Trash2 size={14} /></button>}
          </div>
          {day.exercises.map((ex, ei) => (
            <div key={ei} className="flex flex-wrap gap-2 items-center">
              <Input placeholder="Exercise" value={ex.name} onChange={e => updateExerciseName(di, ei, e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-8 text-xs flex-[3] min-w-[140px]" />
              {(ex.set_groups || [{ sets: "", reps: "" }]).map((g, gi) => (
                <div key={gi} className="flex items-center gap-1 group/sg">
                  <Input placeholder="Sets" value={g.sets} onChange={e => updateSetGroup(di, ei, gi, "sets", e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-8 text-xs w-14" />
                  <span className="text-zinc-700 text-[10px]">×</span>
                  <Input placeholder="Reps" value={g.reps} onChange={e => updateSetGroup(di, ei, gi, "reps", e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-8 text-xs w-14" />
                  {(ex.set_groups || []).length > 1 && (
                    <button onClick={() => removeSetGroup(di, ei, gi)} className="text-zinc-700 hover:text-red-400 opacity-0 group-hover/sg:opacity-100 transition-opacity" title="Remove this set">
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => addSetGroup(di, ei)} className="text-[10px] text-gg-red hover:text-white border border-dashed border-white/10 hover:border-gg-red/40 rounded-lg h-8 px-2 flex items-center gap-1" title="Add another set × reps">
                <Plus size={11} />
              </button>
              <button onClick={() => removeExercise(di, ei)} className="text-zinc-600 hover:text-red-400" title="Remove exercise"><X size={14} /></button>
            </div>
          ))}
          <button onClick={() => addExercise(di)} className="text-[10px] text-gg-red hover:underline flex items-center gap-1"><Plus size={10} /> Add Exercise</button>
        </div>
      ))}
      <button onClick={addDay} className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1"><Plus size={10} /> Add Training Day</button>
      <Button onClick={handleSave} disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="save-workout-btn">
        <Save size={14} className="mr-1" /> {saving ? "..." : "Save Workout Plan"}
      </Button>
    </div>
  );
}

const DietFieldLabel = ({ children }) => <span className="text-zinc-400 text-xs">{children}</span>;
const DietReadCell = ({ children, accent = false }) => (
  <span className={accent ? "text-gg-red font-bold text-sm" : "text-white text-sm font-medium"}>{children}</span>
);

// ==================== DIET EDITOR — KII Case Study Format ====================
const EMPTY_PROFILE = {
  height_feet: "", height_inches: "", gender: "Male", weight_kg: "",
  visual_assessment: "", intensity: "Intermediate", dietary_orientation: "Lacto vegetarian",
  workout_time: "", wake_up_time: "", sleep_time: "", goal: ""
};
const EMPTY_MEAL = { name: "", food_items: "", proteins_g: "", fats_g: "", carbs_g: "", post_meal: "" };

function DietEditor({ token, clientId, existing, onSave }) {
  const [planName, setPlanName] = useState(existing?.plan_name || "");
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE, ...(existing?.profile || {}) });
  const [bodyFatPct, setBodyFatPct] = useState(existing?.calculations?.body_fat_pct || "");
  const [proteinMult, setProteinMult] = useState(existing?.macros?.protein_multiplier || "2");
  const [mealPlan, setMealPlan] = useState((existing?.meal_plan && existing.meal_plan.length > 0) ? existing.meal_plan : [{ ...EMPTY_MEAL, name: "Meal 1" }]);
  const [bedtimeSupplement, setBedtimeSupplement] = useState(existing?.bedtime_supplement || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [saving, setSaving] = useState(false);

  // Auto calculations
  const calcs = useMemo(() => {
    const f = parseFloat(profile.height_feet) || 0;
    const inches = parseFloat(profile.height_inches) || 0;
    const wt = parseFloat(profile.weight_kg) || 0;
    const bf = parseFloat(bodyFatPct) || 0;
    const heightCms = (12 * f * 2.54) + (inches * 2.54);
    const ibwKgs = heightCms > 0 ? heightCms - 100 : 0;
    const lbmKgs = wt > 0 && bf > 0 ? +(wt * (1 - bf / 100)).toFixed(2) : 0;
    const lbmPct = bf > 0 ? +(100 - bf).toFixed(0) : 0;
    const ibwLow = ibwKgs ? +(ibwKgs - 5).toFixed(2) : 0;
    const ibwHigh = ibwKgs ? +(ibwKgs + 5).toFixed(2) : 0;
    const withinRange = wt > 0 && ibwKgs > 0 && wt >= ibwLow && wt <= ibwHigh;
    return {
      height_cms: +heightCms.toFixed(2),
      ibw_kgs: +ibwKgs.toFixed(2),
      ibw_range_low: ibwLow,
      ibw_range_high: ibwHigh,
      body_fat_pct: bf || 0,
      lbm_pct: lbmPct,
      lbm_kgs: lbmKgs,
      within_range: withinRange,
    };
  }, [profile.height_feet, profile.height_inches, profile.weight_kg, bodyFatPct]);

  // Macros
  const macros = useMemo(() => {
    const m = parseFloat(proteinMult) || 0;
    const proteins = calcs.lbm_kgs && m ? +(calcs.lbm_kgs * m).toFixed(1) : 0;
    const mealsCount = mealPlan.length || 1;
    const proteinPerMeal = proteins ? +(proteins / mealsCount).toFixed(2) : 0;
    return {
      protein_multiplier: m,
      proteins_g: proteins,
      fats_g: proteins,
      carbs_g: 30,
      meals_count: mealsCount,
      protein_per_meal: proteinPerMeal,
    };
  }, [proteinMult, calcs.lbm_kgs, mealPlan.length]);

  // Meal totals
  const totals = useMemo(() => {
    const t = { proteins: 0, fats: 0, carbs: 0 };
    mealPlan.forEach((m) => {
      t.proteins += parseFloat(m.proteins_g) || 0;
      t.fats += parseFloat(m.fats_g) || 0;
      t.carbs += parseFloat(m.carbs_g) || 0;
    });
    return { proteins: +t.proteins.toFixed(1), fats: +t.fats.toFixed(1), carbs: +t.carbs.toFixed(1) };
  }, [mealPlan]);

  const updateMeal = (i, field, value) => {
    const m = [...mealPlan];
    m[i] = { ...m[i], [field]: value };
    setMealPlan(m);
  };
  const addMeal = () => setMealPlan([...mealPlan, { ...EMPTY_MEAL, name: `Meal ${mealPlan.length + 1}` }]);
  const removeMeal = (i) => setMealPlan(mealPlan.filter((_, idx) => idx !== i));

  const loadFromPlan = (plan) => {
    if (!plan) return;
    setPlanName(plan.plan_name || "");
    if (plan.profile) setProfile({ ...EMPTY_PROFILE, ...plan.profile });
    if (plan.calculations?.body_fat_pct) setBodyFatPct(plan.calculations.body_fat_pct);
    if (plan.macros?.protein_multiplier) setProteinMult(plan.macros.protein_multiplier);
    if (plan.meal_plan && plan.meal_plan.length > 0) setMealPlan(plan.meal_plan);
    setBedtimeSupplement(plan.bedtime_supplement || "");
    setNotes(plan.notes || "");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api(token).put(`/admin/clients/${clientId}/diet-plan`, {
        plan_name: planName,
        daily_calories: Math.round((macros.proteins_g * 4) + (macros.fats_g * 9) + (macros.carbs_g * 4)),
        meals: mealPlan.map((m) => ({ name: m.name, time: "", items: m.food_items, calories: 0 })), // legacy
        notes,
        profile,
        calculations: calcs,
        macros,
        meal_plan: mealPlan,
        bedtime_supplement: bedtimeSupplement,
      });
      toast.success("Nutrition plan saved!");
      onSave();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const FieldLabel = DietFieldLabel;
  const ReadCell = DietReadCell;

  return (
    <div className="space-y-4" data-testid="diet-editor">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Input placeholder="Plan Name (e.g., Strength Phase, Cut Phase)" value={planName} onChange={(e) => setPlanName(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-10 flex-1" data-testid="diet-plan-name" />
        <CopyFromClient token={token} type="diet" currentClientId={clientId} onCopy={loadFromPlan} />
      </div>

      {/* ========== CASE STUDY (Profile) ========== */}
      <details className="bg-gg-surface rounded-lg border border-white/5" open>
        <summary className="cursor-pointer px-4 py-2.5 text-xs font-heading uppercase tracking-widest text-zinc-300 hover:text-white flex items-center justify-between">
          <span><span className="text-gg-red">1.</span> Member Profile</span>
          <ChevronDown size={14} className="text-zinc-500" />
        </summary>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-white/5">
          <div><FieldLabel>Height</FieldLabel>
            <div className="flex gap-2 mt-1">
              <Input type="number" placeholder="Feet" value={profile.height_feet} onChange={(e) => setProfile({ ...profile, height_feet: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm" />
              <Input type="number" placeholder="Inches" value={profile.height_inches} onChange={(e) => setProfile({ ...profile, height_inches: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm" />
            </div>
          </div>
          <div><FieldLabel>Gender</FieldLabel>
            <select value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })} className="w-full bg-gg-black border border-white/10 text-white rounded-lg h-9 text-sm px-2 mt-1">
              <option>Male</option><option>Female</option>
            </select>
          </div>
          <div><FieldLabel>Weight (kg)</FieldLabel>
            <Input type="number" step="0.1" value={profile.weight_kg} onChange={(e) => setProfile({ ...profile, weight_kg: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm mt-1" />
          </div>
          <div><FieldLabel>Visual Assessment</FieldLabel>
            <Input placeholder="e.g., Slight fat deposition in upper limbs" value={profile.visual_assessment} onChange={(e) => setProfile({ ...profile, visual_assessment: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm mt-1" />
          </div>
          <div><FieldLabel>Intensity</FieldLabel>
            <select value={profile.intensity} onChange={(e) => setProfile({ ...profile, intensity: e.target.value })} className="w-full bg-gg-black border border-white/10 text-white rounded-lg h-9 text-sm px-2 mt-1">
              <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
            </select>
          </div>
          <div><FieldLabel>Dietary Orientation</FieldLabel>
            <Input placeholder="Lacto vegetarian / Non-veg / Vegan" value={profile.dietary_orientation} onChange={(e) => setProfile({ ...profile, dietary_orientation: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm mt-1" />
          </div>
          <div><FieldLabel>Workout Time</FieldLabel>
            <Input placeholder="6 am to 8 am" value={profile.workout_time} onChange={(e) => setProfile({ ...profile, workout_time: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm mt-1" />
          </div>
          <div><FieldLabel>Wake Up Time</FieldLabel>
            <Input placeholder="5 am" value={profile.wake_up_time} onChange={(e) => setProfile({ ...profile, wake_up_time: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm mt-1" />
          </div>
          <div><FieldLabel>Sleep Time</FieldLabel>
            <Input placeholder="9 pm" value={profile.sleep_time} onChange={(e) => setProfile({ ...profile, sleep_time: e.target.value })} className="bg-gg-black border-white/10 text-white rounded-lg h-9 text-sm mt-1" />
          </div>
          <div className="sm:col-span-2"><FieldLabel>Goal</FieldLabel>
            <Textarea placeholder="Wants to increase strength/power, muscle pump and reduce muscle fatigue" value={profile.goal} onChange={(e) => setProfile({ ...profile, goal: e.target.value })} rows={2} className="bg-gg-black border-white/10 text-white rounded-lg text-sm mt-1" />
          </div>
        </div>
      </details>

      {/* ========== CALCULATIONS ========== */}
      <details className="bg-gg-surface rounded-lg border border-white/5" open>
        <summary className="cursor-pointer px-4 py-2.5 text-xs font-heading uppercase tracking-widest text-zinc-300 hover:text-white flex items-center justify-between">
          <span><span className="text-gg-red">2.</span> Calculations (Auto)</span>
          <ChevronDown size={14} className="text-zinc-500" />
        </summary>
        <div className="p-4 border-t border-white/5">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>Height in cms</FieldLabel><ReadCell accent>{calcs.height_cms || "—"}</ReadCell></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>IBW in kgs <span className="text-[10px] text-zinc-600">(height − 100)</span></FieldLabel><ReadCell accent>{calcs.ibw_kgs || "—"}</ReadCell></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>IBW range</FieldLabel><ReadCell accent>{calcs.ibw_range_low} to {calcs.ibw_range_high} kg</ReadCell></div>
            {profile.weight_kg && calcs.ibw_kgs > 0 && (
              <p className={`text-xs italic ${calcs.within_range ? "text-green-400" : "text-orange-400"}`}>
                *Current body weight is {calcs.within_range ? "within" : "outside"} the IBW range.
              </p>
            )}
            <div className="flex justify-between border-b border-white/5 pb-2 pt-2">
              <FieldLabel>Body Fat %</FieldLabel>
              <Input type="number" step="0.1" placeholder="15" value={bodyFatPct} onChange={(e) => setBodyFatPct(e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-xs w-20 text-right" />
            </div>
            <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>LBM %</FieldLabel><ReadCell>{calcs.lbm_pct ? `${calcs.lbm_pct}% (100% − ${bodyFatPct}%)` : "—"}</ReadCell></div>
            <div className="flex justify-between"><FieldLabel>LBM in kgs</FieldLabel><ReadCell accent>{calcs.lbm_kgs || "—"}</ReadCell></div>
          </div>
        </div>
      </details>

      {/* ========== MACROS ========== */}
      <details className="bg-gg-surface rounded-lg border border-white/5" open>
        <summary className="cursor-pointer px-4 py-2.5 text-xs font-heading uppercase tracking-widest text-zinc-300 hover:text-white flex items-center justify-between">
          <span><span className="text-gg-red">3.</span> Macros (Auto)</span>
          <ChevronDown size={14} className="text-zinc-500" />
        </summary>
        <div className="p-4 border-t border-white/5 space-y-2 text-sm">
          <div className="flex justify-between items-center border-b border-white/5 pb-2">
            <FieldLabel>Protein multiplier (×LBM)</FieldLabel>
            <Input type="number" step="0.1" value={proteinMult} onChange={(e) => setProteinMult(e.target.value)} className="bg-gg-black border-white/10 text-white rounded h-7 text-xs w-20 text-right" />
          </div>
          <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>Proteins / day</FieldLabel><ReadCell accent>{macros.proteins_g ? `${macros.lbm_kgs || calcs.lbm_kgs} × ${macros.protein_multiplier} = ${macros.proteins_g} g` : "—"}</ReadCell></div>
          <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>Fats / day</FieldLabel><ReadCell accent>{macros.fats_g ? `${macros.fats_g} g` : "—"}</ReadCell></div>
          <p className="text-zinc-500 text-[11px] italic">*Allowance of up to additional 50 g of fat in the diet. This can be due to &ldquo;difficult-to-quantify&rdquo; invisible fat found in foods such as meat and dairy.</p>
          <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>Carbs (Incidental) / day</FieldLabel><ReadCell>&lt;30 g</ReadCell></div>
          <div className="flex justify-between border-b border-white/5 pb-2"><FieldLabel>Number of meals</FieldLabel><ReadCell>{macros.meals_count}</ReadCell></div>
          <div className="flex justify-between"><FieldLabel>Protein / meal</FieldLabel><ReadCell accent>{macros.protein_per_meal ? `${macros.proteins_g} / ${macros.meals_count} = ${macros.protein_per_meal} g` : "—"}</ReadCell></div>
        </div>
      </details>

      {/* ========== MEAL PLAN TABLE ========== */}
      <details className="bg-gg-surface rounded-lg border border-white/5" open>
        <summary className="cursor-pointer px-4 py-2.5 text-xs font-heading uppercase tracking-widest text-zinc-300 hover:text-white flex items-center justify-between">
          <span><span className="text-gg-red">4.</span> Meal Plan</span>
          <ChevronDown size={14} className="text-zinc-500" />
        </summary>
        <div className="p-3 border-t border-white/5 space-y-3">
          {mealPlan.map((meal, i) => (
            <div key={i} className="bg-gg-black rounded-lg p-3 border border-white/5 space-y-2">
              <div className="flex gap-2 items-center">
                <Input placeholder="Meal name (e.g., Meal 1, Post Workout)" value={meal.name} onChange={(e) => updateMeal(i, "name", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-8 text-xs flex-1" />
                {mealPlan.length > 1 && <button onClick={() => removeMeal(i)} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>}
              </div>
              <Textarea placeholder="Food items (e.g., Whey Isolate — 1.5 scoops + Coconut Oil — 2 tbsp)" value={meal.food_items} onChange={(e) => updateMeal(i, "food_items", e.target.value)} rows={2} className="bg-gg-surface border-white/10 text-white rounded-lg text-xs" />
              <div className="grid grid-cols-3 gap-2">
                <div><span className="text-[9px] text-zinc-500 uppercase">Proteins (g)</span><Input type="number" step="0.1" value={meal.proteins_g} onChange={(e) => updateMeal(i, "proteins_g", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-8 text-xs mt-0.5" /></div>
                <div><span className="text-[9px] text-zinc-500 uppercase">Fats (g)</span><Input type="number" step="0.1" value={meal.fats_g} onChange={(e) => updateMeal(i, "fats_g", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-8 text-xs mt-0.5" /></div>
                <div><span className="text-[9px] text-zinc-500 uppercase">Carbs (g)</span><Input type="number" step="0.1" value={meal.carbs_g} onChange={(e) => updateMeal(i, "carbs_g", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded h-8 text-xs mt-0.5" /></div>
              </div>
              <Input placeholder="Post-meal supplement (optional)" value={meal.post_meal} onChange={(e) => updateMeal(i, "post_meal", e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-8 text-xs" />
            </div>
          ))}
          <button onClick={addMeal} className="text-[10px] text-gg-red hover:underline flex items-center gap-1"><Plus size={10} /> Add Meal</button>

          {/* Totals row */}
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-1 text-xs border-t border-white/10 pt-3 items-baseline">
            <span className="font-heading uppercase tracking-wider text-zinc-400 text-[10px] sm:text-xs">Total</span>
            <span className="text-white whitespace-nowrap"><span className="text-zinc-500">P:</span> {totals.proteins} g</span>
            <span className="text-white whitespace-nowrap"><span className="text-zinc-500">F:</span> {totals.fats} g</span>
            <span className="text-white whitespace-nowrap"><span className="text-zinc-500">C:</span> {totals.carbs} g</span>

            <span className="font-heading uppercase tracking-wider text-zinc-500 text-[10px] sm:text-xs">Recommended</span>
            <span className="text-gg-red whitespace-nowrap">{macros.proteins_g} g</span>
            <span className="text-gg-red whitespace-nowrap">{macros.fats_g} g{macros.fats_g ? <span className="text-zinc-600 text-[10px]"> –{macros.fats_g + 50}g</span> : null}</span>
            <span className="text-gg-red whitespace-nowrap">&lt;30 g</span>
          </div>

          <Input placeholder="Bedtime supplement (e.g., 200 mg magnesium glycinate)" value={bedtimeSupplement} onChange={(e) => setBedtimeSupplement(e.target.value)} className="bg-gg-black border-white/10 text-white rounded-lg h-8 text-xs mt-2" />
        </div>
      </details>

      <Textarea placeholder="Additional notes for the member..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-gg-surface border-white/10 text-white rounded-lg text-xs" />
      <Button onClick={handleSave} disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="save-diet-btn">
        <Save size={14} className="mr-1" /> {saving ? "..." : "Save Nutrition Plan"}
      </Button>
    </div>
  );
}

// ==================== PAYMENT EDITOR ====================
function PaymentEditor({ token, clientId, client, payments, onSave }) {
  const [status, setStatus] = useState(client?.payment_status || "inactive");
  const [planName, setPlanName] = useState(client?.plan_name || "");
  const [amount, setAmount] = useState("");
  const [renewal, setRenewal] = useState(client?.next_renewal || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api(token).put(`/admin/clients/${clientId}/payment`, {
        status, plan_name: planName, amount: parseFloat(amount) || 0, next_renewal: renewal, notes
      });
      toast.success("Payment updated!");
      onSave();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-3" data-testid="payment-editor">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} className="w-full bg-gg-surface border border-white/10 text-white rounded-lg h-10 px-3 text-sm" data-testid="payment-status-select">
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Plan Name</label>
          <Input value={planName} onChange={e => setPlanName(e.target.value)} placeholder="e.g., Monthly Premium" className="bg-gg-surface border-white/10 text-white rounded-lg h-10" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Amount (₹)</label>
          <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className="bg-gg-surface border-white/10 text-white rounded-lg h-10" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-zinc-500 mb-1 block">Next Renewal</label>
          <Input type="date" value={renewal} onChange={e => setRenewal(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-10" />
        </div>
      </div>
      <Textarea placeholder="Payment notes..." value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="bg-gg-surface border-white/10 text-white rounded-lg text-xs" />
      <Button onClick={handleSave} disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="save-payment-btn">
        <Save size={14} className="mr-1" /> {saving ? "..." : "Update Payment"}
      </Button>

      {payments?.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-heading uppercase tracking-wider text-zinc-500 mb-2">Payment History</p>
          <div className="space-y-1">
            {payments.map((p, i) => (
              <div key={p.id || i} className="flex items-center justify-between bg-gg-surface rounded-lg p-2 border border-white/5 text-xs">
                <div>
                  <span className="text-zinc-300">{p.plan_name || "Payment"}</span>
                  <span className="text-zinc-600 ml-2">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <span className="text-gg-red font-bold">{p.amount > 0 ? `₹${p.amount}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== PROGRESS VIEWER ====================
function ProgressViewer({ token, clientId, entries, onSave }) {
  const [form, setForm] = useState({ weight: "", body_fat: "", chest: "", waist: "", hips: "", arms: "", thighs: "", notes: "" });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v && k !== "notes") payload[k] = parseFloat(v);
        else if (v && k === "notes") payload[k] = v;
      });
      await api(token).post(`/admin/clients/${clientId}/progress`, payload);
      toast.success("Progress entry added!");
      setShowForm(false);
      setForm({ weight: "", body_fat: "", chest: "", waist: "", hips: "", arms: "", thighs: "", notes: "" });
      onSave();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="space-y-3" data-testid="progress-viewer">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-heading uppercase tracking-wider text-zinc-500">Progress Entries</p>
        <button onClick={() => setShowForm(!showForm)} className="text-[10px] text-gg-red hover:underline flex items-center gap-1"><Plus size={10} /> Add Entry</button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gg-surface rounded-lg p-3 border border-white/5 space-y-2">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {["weight", "body_fat", "chest", "waist", "hips", "arms", "thighs"].map(f => (
              <div key={f}>
                <label className="text-[9px] uppercase text-zinc-600 block">{f.replace("_", " ")}</label>
                <Input type="number" step="0.1" value={form[f]} onChange={e => setForm({...form, [f]: e.target.value})} className="bg-gg-black border-white/10 text-white rounded h-7 text-xs" />
              </div>
            ))}
          </div>
          <Textarea placeholder="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={1} className="bg-gg-black border-white/10 text-white rounded text-xs" />
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded text-xs h-7">{saving ? "..." : "Save"}</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-zinc-400 text-xs h-7">Cancel</Button>
          </div>
        </form>
      )}

      {entries?.length > 0 ? (
        <div className="space-y-1">
          {entries.map((e, i) => (
            <div key={e.id || i} className="bg-gg-surface rounded-lg p-2 border border-white/5 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-zinc-500">{new Date(e.created_at).toLocaleDateString()}</span>
                {e.added_by === "trainer" && <span className="text-[9px] bg-gg-red/20 text-gg-red px-1.5 py-0.5 rounded">Trainer</span>}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-zinc-300">
                {e.weight && <span>Weight: {e.weight}kg</span>}
                {e.body_fat && <span>BF: {e.body_fat}%</span>}
                {e.chest && <span>Chest: {e.chest}cm</span>}
                {e.waist && <span>Waist: {e.waist}cm</span>}
                {e.hips && <span>Hips: {e.hips}cm</span>}
                {e.arms && <span>Arms: {e.arms}cm</span>}
                {e.thighs && <span>Thighs: {e.thighs}cm</span>}
              </div>
              {e.notes && <p className="text-zinc-500 mt-0.5">{e.notes}</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-zinc-600 text-xs text-center py-4">No progress entries yet</p>
      )}
    </div>
  );
}


// ==================== RESET PASSWORD + CHANGE EMAIL ====================
function ResetPasswordForm({ token, clientId, clientName, clientEmail, onEmailChange }) {
  const [newPassword, setNewPassword] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [newEmail, setNewEmail] = useState(clientEmail || "");
  const [savingEmail, setSavingEmail] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPwd(true);
    try {
      await api(token).put(`/admin/clients/${clientId}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${clientName}`);
      setNewPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
    setSavingPwd(false);
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) { toast.error("Enter a valid email"); return; }
    if (trimmed === (clientEmail || "").toLowerCase()) { toast.error("That's already the current email"); return; }
    setSavingEmail(true);
    try {
      const { data } = await api(token).put(`/admin/clients/${clientId}/change-email`, { new_email: trimmed });
      toast.success(`Email updated to ${data.email}`);
      if (onEmailChange) onEmailChange(data.email);
    } catch (err) { toast.error(err.response?.data?.detail || "Email change failed"); }
    setSavingEmail(false);
  };

  return (
    <div className="space-y-5" data-testid="reset-password-section">
      {/* Change Email */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={14} className="text-gg-red" />
          <p className="text-[10px] font-heading uppercase tracking-wider text-zinc-400">Change email for {clientName}</p>
        </div>
        <form onSubmit={handleEmailChange} className="flex gap-2 items-end">
          <div className="flex-1">
            <Input type="email" placeholder="new.email@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} required
              className="bg-gg-surface border-white/10 text-white rounded-lg h-10" data-testid="admin-change-email-input" />
          </div>
          <Button type="submit" disabled={savingEmail} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg h-10" data-testid="admin-change-email-btn">
            <Mail size={12} className="mr-1" /> {savingEmail ? "..." : "Update"}
          </Button>
        </form>
        <p className="text-zinc-600 text-[10px]">Current: <span className="text-zinc-400">{clientEmail || "—"}</span>. The member will log in with this new email going forward.</p>
      </div>

      {/* Reset Password */}
      <div className="space-y-2 pt-4 border-t border-white/5">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound size={14} className="text-gg-red" />
          <p className="text-[10px] font-heading uppercase tracking-wider text-zinc-400">Reset password for {clientName}</p>
        </div>
        <form onSubmit={handleReset} className="flex gap-2 items-end">
          <div className="flex-1">
            <Input type="text" placeholder="New password (min 6 chars)" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
              className="bg-gg-surface border-white/10 text-white rounded-lg h-10" data-testid="admin-reset-password-input" />
          </div>
          <Button type="submit" disabled={savingPwd} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg h-10" data-testid="admin-reset-password-btn">
            <KeyRound size={12} className="mr-1" /> {savingPwd ? "..." : "Reset"}
          </Button>
        </form>
        <p className="text-zinc-600 text-[10px]">The member will need to use this new password to log in.</p>
      </div>
    </div>
  );
}
